import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const DownloadIndicator = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadComplete, setDownloadComplete] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [customMessage, setCustomMessage] = useState(null);
    const [customCompleteMessage, setCustomCompleteMessage] = useState(null);

    const startDownload = (message = null, completeMessage = null) => {
        setCustomMessage(message);
        setCustomCompleteMessage(completeMessage);
        setIsDownloading(true);
        setDownloadComplete(false);
        setFadeOut(false);
    };

    const completeDownload = () => {
        return new Promise((resolve) => {
            setDownloadComplete(true);
            setTimeout(() => {
                resolve();
                setTimeout(() => {
                    setFadeOut(true);
                    setTimeout(() => {
                        setIsDownloading(false);
                        setDownloadComplete(false);
                        setFadeOut(false);
                        setCustomMessage(null);
                        setCustomCompleteMessage(null);
                    }, 500);
                }, 100);
            }, 400);
        });
    };

    useImperativeHandle(ref, () => ({
        startDownload,
        completeDownload
    }));

    if (!isDownloading) return null;

    return (
        <>
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: fadeOut ? 'transparent' : 'rgba(0, 0, 0, 0.85)',
                    backdropFilter: fadeOut ? 'blur(0px)' : 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    pointerEvents: fadeOut ? 'none' : 'auto'
                }}
            >
                {!downloadComplete ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: 'fadeIn 0.3s ease'
                        }}
                    >
                        <Box
                            sx={{
                                width: '80px',
                                height: '80px',
                                position: 'relative',
                                mb: 4
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    border: '4px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '50%'
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    border: '4px solid transparent',
                                    borderTopColor: '#1976d2',
                                    borderRightColor: '#42a5f5',
                                    borderRadius: '50%',
                                    animation: 'spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite',
                                    filter: 'drop-shadow(0 0 20px rgba(25, 118, 210, 0.8))'
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '50%',
                                    height: '50%',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '50%',
                                    animation: 'pulse 2s ease infinite'
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h5"
                            sx={{
                                color: '#ffffff',
                                fontWeight: 600,
                                letterSpacing: '0.02em',
                                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3), 0 0 30px rgba(255, 255, 255, 0.8)',
                                animation: 'glow 2s ease infinite'
                            }}
                        >
                            {customMessage || t('common.downloadStatus.downloading')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '8px',
                                mt: 3
                            }}
                        >
                            {[0, 1, 2].map((index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: '#42a5f5',
                                        borderRadius: '50%',
                                        animation: 'wave 1.5s ease infinite',
                                        animationDelay: `${index * 0.15}s`,
                                        boxShadow: '0 0 15px rgba(66, 165, 245, 0.8)'
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: fadeOut ? 'morphOut 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards' : 'fadeIn 0.3s ease'
                        }}
                    >
                        <Box
                            sx={{
                                width: '100px',
                                height: '100px',
                                position: 'relative',
                                mb: 4
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                    animation: 'ripple 0.6s ease-out'
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                    animation: 'ripple 0.6s ease-out 0.2s'
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: '80px',
                                    height: '80px',
                                    top: '10px',
                                    left: '10px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    animation: 'scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                    boxShadow: '0 10px 40px rgba(76, 175, 80, 0.4)'
                                }}
                            >
                                <svg
                                    width="50"
                                    height="50"
                                    viewBox="0 0 50 50"
                                    fill="none"
                                    style={{
                                        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                                    }}
                                >
                                    <path
                                        d="M15 25 L22 32 L35 18"
                                        stroke="white"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{
                                            strokeDasharray: '28',
                                            strokeDashoffset: '28',
                                            animation: 'drawCheck 0.4s ease-out 0.3s forwards'
                                        }}
                                    />
                                </svg>
                            </Box>
                        </Box>
                        <Typography
                            variant="h5"
                            sx={{
                                color: '#ffffff',
                                fontWeight: 600,
                                letterSpacing: '0.02em',
                                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3), 0 0 30px rgba(76, 175, 80, 0.8)',
                                animation: 'fadeInUp 0.5s ease 0.2s both'
                            }}
                        >
                            {customCompleteMessage || t('common.downloadStatus.complete')}
                        </Typography>
                    </Box>
                )}
            </Box>
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    @keyframes pulse {
                        0%, 100% { 
                            opacity: 0.3;
                            transform: scale(1);
                        }
                        50% { 
                            opacity: 0.6;
                            transform: scale(1.1);
                        }
                    }
                    @keyframes wave {
                        0%, 60%, 100% { 
                            transform: translateY(0) scale(1);
                            opacity: 0.3;
                        }
                        30% { 
                            transform: translateY(-15px) scale(1.2);
                            opacity: 1;
                        }
                    }
                    @keyframes glow {
                        0%, 100% { 
                            opacity: 0.8;
                        }
                        50% { 
                            opacity: 1;
                        }
                    }
                    @keyframes fadeIn {
                        from { 
                            opacity: 0; 
                            transform: scale(0.95);
                        }
                        to { 
                            opacity: 1; 
                            transform: scale(1);
                        }
                    }
                    @keyframes scaleIn {
                        from { 
                            transform: scale(0) rotate(180deg);
                            opacity: 0;
                        }
                        to { 
                            transform: scale(1) rotate(0);
                            opacity: 1;
                        }
                    }
                    @keyframes ripple {
                        from {
                            transform: scale(0.8);
                            opacity: 1;
                        }
                        to {
                            transform: scale(2);
                            opacity: 0;
                        }
                    }
                    @keyframes drawCheck {
                        to {
                            stroke-dashoffset: 0;
                        }
                    }
                    @keyframes fadeInUp {
                        from { 
                            opacity: 0; 
                            transform: translateY(20px);
                        }
                        to { 
                            opacity: 1; 
                            transform: translateY(0);
                        }
                    }
                    @keyframes morphOut {
                        0% { 
                            transform: scale(1) translateY(0);
                            opacity: 1;
                            filter: saturate(1) brightness(1);
                        }
                        20% {
                            transform: scale(0.98) translateY(-5px);
                            opacity: 1;
                            filter: saturate(1.1) brightness(1.05);
                        }
                        40% {
                            transform: scale(0.95) translateY(-15px);
                            opacity: 0.95;
                            filter: saturate(1.2) brightness(1.1);
                        }
                        60% {
                            transform: scale(0.9) translateY(-25px);
                            opacity: 0.7;
                            filter: saturate(0.9) brightness(1.3);
                        }
                        80% {
                            transform: scale(0.5) translateY(-40px);
                            opacity: 0.3;
                            filter: saturate(0.5) brightness(1.6);
                        }
                        100% { 
                            transform: scale(0) translateY(-50px);
                            opacity: 0;
                            filter: saturate(0) brightness(2);
                        }
                    }
                `}
            </style>
        </>
    );
});

DownloadIndicator.displayName = 'DownloadIndicator';

export default DownloadIndicator;